import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from 'tss-react/mui';

import Tooltip from '~/components/core/Atomic/Tooltip';
import cn from '~/Utils/cn';

// Module scope: defined inside the component this re-created the hook (and re-compiled the
// styles) on every render.
const useStyles = makeStyles()((_theme, { tooltipMaxWidth }) => ({
  customWidth: {
    maxWidth: tooltipMaxWidth || null,
  },
}));

function OverflowTextWithToolTip({
  children,
  textWrapperClassName,
  maxWidth,
  disabled,
  tooltipMaxWidth,
  tooltipPlacement,
  tooltipTitle,
  tooltipClassName,
  arrow = false,
}) {
  const textDivElement = React.useRef();
  const [isOverflowed, setIsOverflowed] = useState(false);

  const recomputeOverflowed = useCallback(() => {
    const element = textDivElement.current;
    if (element) {
      setIsOverflowed(element.scrollWidth > element.clientWidth);
    }
  }, []);

  // A `children` change overflows the element without resizing its box, so the ResizeObserver
  // below won't fire for it — recompute explicitly when the content changes.
  useEffect(() => {
    if (disabled) {
      return;
    }
    recomputeOverflowed();
  }, [disabled, children, recomputeOverflowed]);

  // Observe the element's size so the tooltip stays in sync as the layout resizes. It catches any
  // resize regardless of content, so it's wired once and doesn't depend on `children`.
  useEffect(() => {
    const element = textDivElement.current;
    if (disabled || !element || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(recomputeOverflowed);
    observer.observe(element);
    return () => observer.disconnect();
  }, [disabled, recomputeOverflowed]);

  const { classes } = useStyles({ tooltipMaxWidth });

  if (disabled) {
    return children;
  }

  return (
    // min-w-0 so this can shrink when it's a flex item (e.g. inside a flex container): without it
    // the default `min-width: auto` keeps it at its content width and a long value overflows the
    // column. No-op outside flex contexts.
    <div className="min-w-0">
      <Tooltip
        title={tooltipTitle ?? children}
        arrow={arrow}
        disableHoverListener={!isOverflowed}
        placement={tooltipPlacement}
        classes={
          tooltipMaxWidth || tooltipClassName
            ? { tooltip: cn(tooltipMaxWidth && classes.customWidth, tooltipClassName) }
            : undefined
        }
        // This tooltip re-shows text that is already on screen (truncated), so it *describes* the
        // child rather than naming it. Without `describeChild`, MUI v5 puts the text on the child as
        // `aria-label`, which becomes the accessible name — and where this renders inside a field's
        // <label>, that name then competes with the field's own. v4 used the native `title`, which
        // is what `describeChild` restores.
        describeChild
      >
        <div
          ref={textDivElement}
          data-overflowing={isOverflowed}
          className={cn('truncate', textWrapperClassName)}
          style={{ maxWidth }}
        >
          {children}
        </div>
      </Tooltip>
    </div>
  );
}

OverflowTextWithToolTip.propTypes = {
  children: PropTypes.node.isRequired,
  textWrapperClassName: PropTypes.string,
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tooltipMaxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tooltipPlacement: PropTypes.string,
  tooltipTitle: PropTypes.node,
  tooltipClassName: PropTypes.string,
  disabled: PropTypes.bool,
  arrow: PropTypes.bool,
};

export default OverflowTextWithToolTip;
